import React from "react";

// Wraps the routed page content (see Layout.jsx), so it trips whenever a tab
// fails to render. Historically it immediately showed a "Page View
// Initializing / The data view is updating" notice and made the person using
// the page click a button to reload — i.e. the refresh was fully visible to
// them. Instead: remount the subtree once, silently, and only surface the
// notice if that single attempt did not fix it. The `retried` flag guarantees
// at most one automatic attempt, so a genuinely broken view still surfaces
// rather than looping forever.
const AUTO_RETRY_MS = 150;

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, retried: false };
    this._retryTimer = null;
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Tab Navigation Error Catch:", error, errorInfo);
    if (this.state.retried) return;
    this._retryTimer = setTimeout(() => {
      this.setState({ hasError: false, error: null, retried: true });
    }, AUTO_RETRY_MS);
  }

  componentWillUnmount() {
    clearTimeout(this._retryTimer);
  }

  render() {
    if (this.state.hasError) {
      // A silent retry is already queued: stay blank for a fraction of a
      // second instead of flashing a reload screen at the user. If the retry
      // fails, `retried` is true and we fall through to the notice below.
      if (!this.state.retried) return null;

      return (
        <div className="p-8 max-w-xl mx-auto my-12 neu-card text-center space-y-4">
          <div className="text-amber-800 text-sm font-extrabold uppercase tracking-widest">
            Tab Reload Notice
          </div>
          <h2 className="text-xl font-bold text-slate-900">
            Page View Initializing
          </h2>
          <p className="text-xs text-slate-600 font-medium">
            The data view is updating. Click below to refresh this tab view cleanly.
          </p>
          {this.state.error && (
            <details className="text-left bg-white border border-slate-200 rounded-xl p-3">
              <summary className="text-[11px] font-bold text-slate-500 uppercase cursor-pointer">Error details (share this)</summary>
              <pre className="text-[11px] text-rose-700 font-mono whitespace-pre-wrap mt-2">
                {String(this.state.error?.message || this.state.error)}
              </pre>
            </details>
          )}
          <div className="flex items-center justify-center gap-3 pt-2">
            <button
              onClick={() => {
                this.setState({ hasError: false, error: null, retried: true });
              }}
              className="px-5 py-2.5 bg-slate-950 text-amber-300 font-bold text-xs rounded-xl shadow-md hover:bg-slate-900 transition-all"
            >
              Reload Tab View
            </button>
            <button
              onClick={() => {
                window.location.reload();
              }}
              className="px-4 py-2.5 bg-white text-slate-700 font-bold text-xs rounded-xl border border-slate-300 hover:bg-slate-50 transition-all"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
